import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export type LandingPreference = "dashboard" | "admin" | "instructor" | null;

const PATHS: Record<Exclude<LandingPreference, null>, string> = {
  dashboard: "/dashboard",
  admin: "/admin",
  instructor: "/instructor/dashboard",
};

/**
 * Resolve the post-login landing path for a user.
 *
 * Precedence:
 *  1. Saved preference on profiles.default_landing (if set).
 *  2. Admin AND has zero enrollments  → /admin (pure admin account).
 *  3. Instructor (and not admin)      → /instructor/dashboard.
 *  4. Otherwise                       → /dashboard.
 *
 * Designed to be cheap (3 small queries) and safe (every branch returns a
 * valid path; never throws).
 */
export async function resolveDefaultLanding(userId: string): Promise<string> {
  if (!userId) return "/dashboard";

  try {
    const [{ data: profile }, { data: roles }, { count: enrollmentCount }] =
      await Promise.all([
        supabase
          .from("profiles")
          .select("default_landing")
          .eq("id", userId)
          .maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase
          .from("enrollments")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId),
      ]);

    const saved = (profile?.default_landing ?? null) as LandingPreference;
    if (saved && PATHS[saved]) return PATHS[saved];

    const isAdmin = roles?.some((r) => r.role === "admin") ?? false;
    const isInstructor = roles?.some((r) => r.role === "instructor") ?? false;
    const hasEnrollments = (enrollmentCount ?? 0) > 0;

    if (isAdmin && !hasEnrollments) return PATHS.admin;
    if (isInstructor && !isAdmin) return PATHS.instructor;
    return PATHS.dashboard;
  } catch (error) {
    logger.error("resolveDefaultLanding failed", error);
    return "/dashboard";
  }
}

/** Absolute URL variant for use in `emailRedirectTo`. */
export function landingUrlFor(path: string): string {
  return `${window.location.origin}${path}`;
}
